"use client";

/**
 * Shared pieces reused by both /v3/a and /v3/b, extracted (never edited-in-
 * place) from app/v2/V2Client.tsx per the build spec — app/v2/ itself is
 * never touched.
 *
 * Ports as-is:
 *   - AskChip — the copy-to-clipboard suggested-question chip. Used inside
 *     ShowcaseRowV3.tsx's "Ask it" pane (see ./ShowcaseRowV3.tsx).
 *   - <PhoneBand /> — the phone-band JSX from V2Client's hero section,
 *     copied verbatim (copy lives in ./content.ts).
 *   - <ReusedProductBlock /> — <ShowcaseRowV3 /> + <PhoneBand />, Drawer 02
 *     complete.
 *   - <ReusedTrustBlock /> — thin wrapper around the imported <GlassEngine />
 *     and <AdvisorDesk /> (Drawers 03/04), unchanged.
 *   - REUSE_CSS — the subset of V2Client's CSS needed to style ShowcaseRowV3
 *     / GlassEngine / AdvisorDesk / the phone band, rescoped from
 *     `.v2root .a` to `.v3reuse` so it can be dropped into either
 *     variation's stylesheet without depending on app/v2/'s own root class.
 *     Selectors and rule bodies are otherwise copied unchanged, including
 *     the pre-existing v2 quirk where `.comp` is declared twice (compliance
 *     box + engine readout) — preserved here for visual parity, not "fixed."
 *
 * Removed per Jay's review round 2: the standalone "Ask her portfolio"
 * panel and the ElevenLabs voice-widget dock it hosted. The widget rendered
 * as a floating orb fixed to the viewport corner regardless of where its
 * host tag sat in the DOM (a known, never-fully-resolved /v2 quirk — see
 * V2Client.tsx's own comments on it) and floated over the hero on /v3. Jay
 * asked for it gone entirely, not just re-docked. The suggested-question
 * chips moved into ShowcaseRowV3's "Ask it" pane instead (click-to-copy,
 * no live typing/voice) — see ./ShowcaseRowV3.tsx.
 */

import { GlassEngineV3 } from "./GlassEngineV3";
import { AdvisorDesk } from "../v2/AdvisorDesk";
import { CALENDLY_URL, PHONE_BAND, PHONE_TEL, PHONE_DISPLAY } from "./content";
import { ShowcaseRowV3 } from "./ShowcaseRowV3";

export { CALENDLY_URL };

// -----------------------------------------------------------------------
// renderInline — parses `**bold**` spans out of content.ts strings into
// <strong> elements. Mirrors how /v2's JSX used <b>/<em> for the same
// spans (both render as bold+color there, not italics).
// -----------------------------------------------------------------------
export function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

/* AskChip (copy-to-clipboard suggested question) was removed in Round 7. The
 * Ask it pane runs a real conversation now, so its chips ASK the question
 * instead of copying it to the clipboard, and they live with the chat that
 * sends them — see ./ShowcaseRowV3.tsx. app/v2/ keeps its own local copy of
 * the old chip and is untouched. */

/** Drawer 02 — the phone promotion band, verbatim from /v2. */
export function PhoneBand() {
  return (
    <section className="phoneband">
      <div className="phoneband-inner">
        <p className="phoneband-h">{PHONE_BAND.heading}</p>
        <a className="phoneband-tel" href={PHONE_TEL}>
          {PHONE_DISPLAY}
        </a>
        <p className="phoneband-sub">{renderInline(PHONE_BAND.sub)}</p>
        <p className="phoneband-sim">{PHONE_BAND.simNote}</p>
      </div>
    </section>
  );
}

/** Drawer 02, complete — <ShowcaseRowV3 /> + phone band. The ask surface is
 * fully inside the showcase row's "Ask it" pane now (see item 2 of the
 * round-2 review); there is no separate ask panel to render here anymore.
 * Wrap this in a `.v3reuse` container and include REUSE_CSS in the page's
 * stylesheet. */
export function ReusedProductBlock() {
  return (
    <>
      <ShowcaseRowV3 />
      <PhoneBand />
    </>
  );
}

/** Drawers 03 + 04 — the forked, looping <GlassEngineV3 /> then the
 * unchanged <AdvisorDesk />. GlassEngineV3 renders the same `.engine` class
 * names as the v2 original, so it's styled entirely by REUSE_CSS below. */
export function ReusedTrustBlock() {
  return (
    <>
      <GlassEngineV3 />
      <AdvisorDesk />
    </>
  );
}

// ---------------------------------------------------------------------------
// REUSE_CSS — styles for the components above, rescoped from `.v2root .a`
// to `.v3reuse`. Copied from app/v2/V2Client.tsx's CSS block (never edited
// there); this is the ported subset, not the whole page stylesheet. The
// "Ask it" pane's chip styling (below, under "Weighted four-door showcase
// row") is new — it replaces the old standalone ask-panel CSS that shipped
// in round 1 and has since been deleted along with the widget.
// ---------------------------------------------------------------------------
export const REUSE_CSS = `
  .v3reuse, .v3reuse * { box-sizing: border-box; }
  .v3reuse {
    --serif-memo: "Charter", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif;
    --sans-sys: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    --mono: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-family: var(--sans-sys);
  }
  .v3reuse .lbl { font-size:11px; letter-spacing:1.4px; text-transform:uppercase; color:#16324F; font-weight:700; margin-bottom:26px; }

  /* Phone band — slimmed in Round 7. It was 290px of navy for one phone
     number: 48px padding, a 40px numeral and a 20px heading, which put it in
     the same weight class as the product row directly above it. The line is
     a supporting proof point, not a second hero, so the whole band is now
     about a third shorter and the number sits closer to body scale. */
  .v3reuse .phoneband { background:#16324F; padding:0; }
  .v3reuse .phoneband-inner { max-width:640px; margin:0 auto; padding:30px 40px; text-align:center; }
  @media(max-width:640px){ .v3reuse .phoneband-inner{ padding:26px 22px; } }
  .v3reuse .phoneband-h { font-family:var(--serif-memo); font-size:17px; font-weight:600; color:#F4F1E9; margin-bottom:10px; }
  .v3reuse .phoneband-tel { display:block; font-family:var(--serif-memo); font-weight:700; font-size:clamp(21px,2.4vw,27px); color:#F4F1E9; text-decoration:none; letter-spacing:-.3px; margin-bottom:10px; }
  .v3reuse .phoneband-tel:hover { color:#C9A968; }
  .v3reuse .phoneband-sub { font-size:12.5px; color:#C9D2DC; line-height:1.55; max-width:56ch; margin:0 auto; }
  .v3reuse .phoneband-sub strong { color:#F4F1E9; }
  .v3reuse .phoneband-sim { font-size:10.5px; color:#8FA1B5; margin-top:10px; }

  /* -------------------------------------------------------------------
     Four-door showcase — HORIZONTAL ACCORDION (rebuilt round 4).
     Exactly one pane is expanded; the rest are fixed 64px strips showing a
     vertical title. Jay rejected the previous flex-grow-on-hover version:
     collapsed panes truncated titles to a single letter, and the video
     didn't fill its pane. This is the standard accordion instead.
     ------------------------------------------------------------------- */
  .v3reuse .showcase { display:flex; gap:10px; margin-top:44px; height:min(58vh,560px); width:100%; }

  /* Every pane holds a fixed 64px flex-basis; only the expanded pane grows to
     fill the row. Animating flex-grow (0 -> 1) interpolates the width
     smoothly while the collapsed panes stay locked at 64px (flex-shrink:0) —
     the canonical horizontal-accordion technique, no width/JS animation. */
  .v3reuse .pane { position:relative; flex:0 0 64px; overflow:hidden;
          border:1px solid #E6E1D6; background:#fff; display:flex; flex-direction:column;
          transition:flex-grow .45s cubic-bezier(.4,0,.2,1); }
  .v3reuse .pane.expanded { flex-grow:1; }
  .v3reuse .pane.video { border-top:3px solid #16324F; }
  .v3reuse .pane.phone { background:#16324F; border-color:#16324F; color:#F4F1E9; }

  /* The trigger is a real <button> (click + keyboard + aria-expanded).
     Collapsed, it fills the whole strip so the entire strip is one
     hover/click/focus target with the vertical title centered in it.
     Expanded, it shrinks to just the header row above the body. */
  .v3reuse .panetrigger { appearance:none; -webkit-appearance:none; margin:0; padding:0;
          font:inherit; color:inherit; background:transparent; border:none; text-align:left;
          cursor:pointer; width:100%; position:relative; z-index:2; }
  .v3reuse .pane.collapsed .panetrigger { position:absolute; inset:0; }
  .v3reuse .panetrigger:focus-visible { outline:2px solid #9C7A3C; outline-offset:-3px; }
  .v3reuse .pane.phone .panetrigger:focus-visible { outline-color:#C9A85C; }

  /* Vertical strip title — the collapsed state on desktop. Full title, never
     truncated (writing-mode: vertical-rl), plus the small expand glyph. */
  .v3reuse .striptitle { position:absolute; inset:0; display:flex; flex-direction:column;
          align-items:center; justify-content:center; gap:12px; padding:14px 0; }
  .v3reuse .striptitle .stt { writing-mode:vertical-rl; font-family:var(--serif-memo);
          font-size:15px; font-weight:700; letter-spacing:.3px; color:#16324F; white-space:nowrap; }
  .v3reuse .striptitle .stglyph { font-size:11px; color:#9AA1AB; line-height:1; }
  .v3reuse .pane.phone .striptitle .stt { color:#F4F1E9; }
  .v3reuse .pane.phone .striptitle .stglyph { color:#8FA1B5; }
  .v3reuse .pane.expanded .striptitle { opacity:0; pointer-events:none; }

  /* Horizontal header row — shown when expanded (and, on mobile, always: it
     becomes the tappable bar). */
  .v3reuse .pane .head { display:flex; align-items:baseline; justify-content:space-between;
          gap:8px; padding:12px 16px; border-bottom:1px solid #E6E1D6; white-space:nowrap; }
  .v3reuse .pane.collapsed .head { display:none; }
  .v3reuse .pane .head .t { font-family:var(--serif-memo); font-size:16px; font-weight:700; color:#16324F;
          overflow:hidden; text-overflow:ellipsis; }
  .v3reuse .pane .head .headright { display:flex; align-items:center; gap:6px; flex-shrink:0; }
  .v3reuse .pane .head .k { font-family:var(--mono); font-size:9px; letter-spacing:1.5px;
          text-transform:uppercase; color:#9C7A3C; font-weight:700; white-space:nowrap; }
  .v3reuse .pane .head .k.comingtag { color:#55607a; border:1px solid #C9BEA6; padding:2px 6px; border-radius:3px; }
  .v3reuse .pane .head .expand { font-size:10px; color:#9AA1AB; line-height:1; }
  .v3reuse .pane.phone .head { border-bottom-color:#2A4668; }
  .v3reuse .pane.phone .head .t { color:#F4F1E9; }
  .v3reuse .pane.phone .head .expand { color:#8FA1B5; }

  /* Body — visible only when the pane is expanded; fades + slides in briefly.
     When collapsed on desktop it's transparent (the pane's own background and
     the absolute trigger's vertical title show instead) and non-interactive. */
  .v3reuse .pane .body { flex:1; position:relative; overflow:hidden;
          transition:opacity .3s ease .1s, transform .3s ease .1s; }
  .v3reuse .pane.collapsed .body { opacity:0; pointer-events:none; transform:translateY(6px); }

  /* Show the WHOLE video frame at all times — object-fit:contain, centered on
     a deliberate dark screening-room matte (round-5 correction: cover cropped
     the frame for mismatched aspect ratios). The matte (ink-navy #0B1520) fills
     the letterbox bars on the short axis so they read as an intentional cinema
     treatment, not dead space. */
  .v3reuse .pane.video .body { background:#0B1520; }
  .v3reuse .pane.video .body video { position:absolute; inset:0; width:100%; height:100%; object-fit:contain; object-position:center; display:block; background:transparent; }
  .v3reuse .pane.video .cap { position:absolute; left:0; right:0; bottom:0; background:linear-gradient(transparent,#14202Cd9); color:#fff; font-family:var(--mono); font-size:10px; letter-spacing:1px; text-transform:uppercase; font-weight:700; padding:26px 14px 10px; pointer-events:none; }

  /* Click-to-play (Round 7). A poster under native controls reads as a still
     image on a pane this wide — nothing said it plays. Shown before the first
     play and again after it ends; NOT on a manual pause. */
  .v3reuse .pane.video .playover {
    position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
    display:flex; align-items:center; gap:12px; cursor:pointer;
    font:inherit; color:#F4F1E9;
    background:rgba(11,21,32,.72); border:1px solid rgba(244,241,233,.5);
    border-radius:999px; padding:12px 24px 12px 18px;
    backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px);
    transition:background-color .18s, border-color .18s;
  }
  .v3reuse .pane.video .playover:hover { background:rgba(11,21,32,.88); border-color:#F4F1E9; }
  .v3reuse .pane.video .playover:focus-visible { outline:2px solid #C9A85C; outline-offset:3px; }
  .v3reuse .pane.video .playtri { width:0; height:0; border-style:solid; border-width:9px 0 9px 15px; border-color:transparent transparent transparent #F4F1E9; }
  .v3reuse .pane.video .playtxt { font-family:var(--mono); font-size:12px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; }

  /* ── "Ask it" — a real conversation (Round 7) ──────────────────────
     This pane used to be two hardcoded bubbles and chips that copied the
     question to your clipboard. It now runs the same working text Q&A the
     /fideo page has always had, against the same proxy route. The pane is
     short, so the thread scrolls and everything else is fixed height. */
  .v3reuse .pane.ask .body { padding:14px; display:flex; flex-direction:column; gap:8px; background:#FCFAF5; }
  .v3reuse .askchat { display:flex; flex-direction:column; gap:9px; min-height:0; flex:1; }
  .v3reuse .askthread { flex:1; min-height:0; overflow-y:auto; display:flex; flex-direction:column; gap:8px; }
  .v3reuse .askempty { font-family:var(--serif-memo); font-style:italic; font-size:12.5px; line-height:1.5; color:#55607a; }
  .v3reuse .pane.ask .bub { max-width:88%; font-size:12.5px; line-height:1.5; padding:8px 12px; border:1px solid #E6E1D6; background:#fff; }
  .v3reuse .pane.ask .bub strong { color:#16324F; font-weight:700; }
  .v3reuse .pane.ask .bub.q { align-self:flex-end; background:#16324F; color:#F4F1E9; border-color:#16324F; }
  .v3reuse .pane.ask .bub.q strong { color:#F4F1E9; }
  .v3reuse .pane.ask .bub .cite { display:block; margin-top:6px; padding-top:5px; border-top:1px solid #E6E1D6; font-family:var(--mono); font-size:9px; letter-spacing:.5px; color:#1E6B52; }

  /* Thinking indicator. Three dots rather than a spinner: the answer is
     being computed, and a spinner reads as "loading a page". */
  .v3reuse .pane.ask .bub.thinking { display:flex; align-items:center; gap:5px; padding:11px 12px; }
  .v3reuse .pane.ask .bub.thinking .dot { width:5px; height:5px; border-radius:50%; background:#9C7A3C; opacity:.35; animation:v3think 1.1s infinite; }
  .v3reuse .pane.ask .bub.thinking .dot:nth-child(2) { animation-delay:.16s; }
  .v3reuse .pane.ask .bub.thinking .dot:nth-child(3) { animation-delay:.32s; }
  @keyframes v3think { 0%,60%,100%{opacity:.28;} 30%{opacity:1;} }

  .v3reuse .pane.ask .asktry-lbl { display:block; font-size:9.5px; letter-spacing:.6px; text-transform:uppercase; color:#9C7A3C; font-weight:700; margin-bottom:6px; }
  .v3reuse .pane.ask .askchips { display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
  .v3reuse .pane.ask .achip { font-family:inherit; font-size:10.5px; color:#16324F; background:#fff; border:1px solid #C9BEA6; border-radius:999px; padding:4px 9px; cursor:pointer; transition:background-color .15s, border-color .15s, opacity .15s; }
  .v3reuse .pane.ask .achip:hover:not(:disabled) { background:#FBFAF7; border-color:#9C7A3C; }
  .v3reuse .pane.ask .achip:focus-visible { outline:2px solid #9C7A3C; outline-offset:2px; }
  .v3reuse .pane.ask .achip:disabled { cursor:default; opacity:.5; }
  .v3reuse .pane.ask .achip.used { color:#9AA1AB; border-color:#E6E1D6; }
  /* Ask count on a chip (blog notes only): "18×" in gold mono. */
  .v3reuse .pane.ask .achipn { margin-left:6px; font-family:var(--mono); font-size:9px; letter-spacing:.3px; color:#9C7A3C; font-weight:700; }
  /* Rate-limited reply carries a demo link. */
  .v3reuse .pane.ask .bublink { display:inline-block; margin-top:6px; font-weight:600; color:#16324F; text-decoration:underline; text-underline-offset:2px; text-decoration-color:#9C7A3C; }

  .v3reuse .askform { display:flex; gap:6px; }
  .v3reuse .askinput { flex:1; min-width:0; font-family:inherit; font-size:12px; color:#14202C; background:#fff; border:1px solid #C9BEA6; border-radius:2px; padding:8px 10px; }
  .v3reuse .askinput::placeholder { color:#9AA1AB; }
  .v3reuse .askinput:focus { outline:none; border-color:#16324F; box-shadow:0 0 0 2px #16324F22; }
  .v3reuse .asksend { flex:none; font-family:inherit; font-size:12px; font-weight:600; color:#F4F1E9; background:#16324F; border:1px solid #16324F; border-radius:2px; padding:8px 14px; cursor:pointer; }
  .v3reuse .asksend:disabled { background:#9AA1AB; border-color:#9AA1AB; cursor:default; }
  .v3reuse .asksend:focus-visible { outline:2px solid #9C7A3C; outline-offset:2px; }

  /* Voice is opt-in. The ElevenLabs embed renders fixed to the viewport
     corner from inside its own shadow DOM and cannot be docked into this
     pane, so it only mounts when someone presses this. */
  .v3reuse .askvoice { align-self:flex-start; font-family:inherit; font-size:11.5px; font-weight:600; color:#9C7A3C; background:none; border:none; padding:0; cursor:pointer; text-decoration:underline; text-underline-offset:2px; }
  .v3reuse .askvoice:hover { color:#16324F; }
  .v3reuse .askvoice:focus-visible { outline:2px solid #9C7A3C; outline-offset:2px; }

  .v3reuse .paneguard { font-size:10.5px; color:#1E6B52; line-height:1.4; margin-top:6px; }
  .v3reuse .panesim { font-size:10px; color:#9AA1AB; margin-top:2px; }
  .v3reuse .askcode { font-size:11.5px; color:#55607a; margin-top:4px; }
  .v3reuse .askcode b { color:#9C7A3C; font-weight:700; letter-spacing:.3px; }

  /* "Hear it" — the real audio edition player (round-6). preload="none", so
     the pane costs nothing until pressed; native controls, sized to the pane. */
  .v3reuse .pane.podcast .body { padding:16px 14px; display:flex; flex-direction:column; gap:11px; background:#FCFAF5; }
  .v3reuse .hearhead { display:flex; flex-direction:column; gap:5px; }
  .v3reuse .heart { font-family:var(--serif-memo); font-size:15px; font-weight:700; color:#16324F; line-height:1.3; }
  .v3reuse .heark { font-family:var(--mono); font-size:9px; letter-spacing:1.2px; text-transform:uppercase; color:#9C7A3C; font-weight:700; }
  /* Audiogram + player: waveform above the native controls. */
  .v3reuse .hearplayer { display:flex; flex-direction:column; gap:8px; }
  .v3reuse .hearwave { width:100%; height:64px; display:block; cursor:pointer; }
  .v3reuse .hearaudio { width:100%; }
  .v3reuse .hearline { font-size:11.5px; color:#55607a; line-height:1.45; }
  .v3reuse .pane.podcast .panesim { margin-top:auto; }

  .v3reuse .pane.phone .body { padding:14px 13px; display:flex; flex-direction:column; }
  .v3reuse .phbig { font-family:var(--serif-memo); font-size:clamp(13px,1.2vw,19px); font-weight:700; line-height:1.35; }
  /* The number is a tap-to-dial tel: link (the whole pane used to be one; now
     the strip is the accordion trigger, so the number carries the dial). */
  .v3reuse .phnum { margin-top:auto; display:block; text-decoration:none; color:inherit; }
  .v3reuse .phnum:hover .n { color:#C9A85C; }
  .v3reuse .phnum .n { font-family:var(--serif-memo); font-size:clamp(11px,.95vw,15px); font-weight:700; color:#fff; letter-spacing:-.2px; transition:color .15s; }
  .v3reuse .phnum .c { font-family:var(--mono); font-size:9px; letter-spacing:1px; color:#C9A85C; margin-top:6px; text-transform:uppercase; }
  .v3reuse .phguard { font-size:10.5px; color:#C9D2DC; line-height:1.4; margin-top:6px; }

  /* Persistent discoverability hint, directly under the row — plain-text
     wording swap by viewport width, same breakpoint the row itself stacks
     at (below that, panes are already full-width and always "open"). */
  .v3reuse .showhint { text-align:center; font-family:var(--mono); font-size:10.5px; letter-spacing:.8px; text-transform:uppercase; color:#9AA1AB; margin-top:16px; }
  .v3reuse .showhint-touch { display:none; }
  @media(max-width:900px){
    .v3reuse .showhint-desktop { display:none; }
    .v3reuse .showhint-touch { display:inline; }
  }

  .v3reuse .showsub { text-align:center; font-size:12.5px; color:#9AA1AB; margin-top:12px; }
  .v3reuse .showsub b { color:#55607a; }

  /* min-width:0 lets inner head/body content shrink and clip cleanly during
     the collapse. Panes themselves hold at 64px (flex-shrink:0), unaffected. */
  .v3reuse .showcase, .v3reuse .showcase * { min-width: 0; }

  /* Mobile (<900px): VERTICAL accordion — four stacked header bars, exactly
     one open. The vertical strip title is desktop-only; here the head is the
     always-visible tappable bar, and the body shows only when expanded. No
     opening choreography on mobile (disabled in the hook). */
  @media(max-width:900px){
    .v3reuse .showcase { flex-direction:column; height:auto; gap:8px; }
    .v3reuse .pane { flex:0 0 auto; }
    .v3reuse .striptitle { display:none; }
    .v3reuse .pane.collapsed .panetrigger { position:relative; inset:auto; }
    .v3reuse .pane.collapsed .head { display:flex; }
    /* flex:0 0 auto resets the desktop flex:1 (flex-basis:0%) — otherwise the
       zero basis overrides the explicit video height below and the body
       collapses to 0 (flex-basis beats height/width for flex items). */
    .v3reuse .pane .body { flex:0 0 auto; transition:none; transform:none; }
    /* Collapsed body hidden entirely (4-class selector, placed after the
       per-pane .pane.ask .body etc. rules so it wins the specificity tie);
       expanded bodies keep their own display value from the rules above. */
    .v3reuse .pane.collapsed .body { display:none; }
    .v3reuse .pane.video .body { height:56vw; max-height:300px; }
  }

  /* The glass engine */
  .v3reuse .engine, .v3reuse .engine * { min-width: 0; }
  .v3reuse .desk, .v3reuse .desk * { min-width: 0; }

  .v3reuse .engine { margin-top:56px; border-top:1px solid #E6E1D6; padding:44px 0 60px; }
  .v3reuse .engine .lbl { font-family:var(--mono); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:#16324F; font-weight:700; text-align:center; }
  .v3reuse .engine h2 { font-family:var(--serif-memo); font-size:clamp(22px,2.6vw,32px); font-weight:600; text-align:center; margin-top:10px; letter-spacing:-.4px; }
  .v3reuse .engine .sub { text-align:center; color:#55607a; font-size:14px; max-width:64ch; margin:12px auto 0; }
  .v3reuse .modes { display:flex; gap:12px; justify-content:center; margin-top:20px; flex-wrap:wrap; }
  .v3reuse .mode { font-size:12px; color:#55607a; border:1px solid #E6E1D6; background:#fff; padding:8px 14px; border-radius:999px; }
  .v3reuse .mode b { color:#16324F; }
  .v3reuse .mode.on { border-color:#9C7A3C; background:#FBF6EA; }
  .v3reuse .machine { margin:36px auto 0; border:1px solid #E6E1D6; background:#FCFAF5; }
  .v3reuse .runline { display:flex; justify-content:space-between; align-items:center; gap:14px; flex-wrap:wrap; padding:9px 16px; border-bottom:1px solid #E6E1D6; font-family:var(--mono); font-size:10px; letter-spacing:1px; text-transform:uppercase; }
  .v3reuse .runline .rid { color:#55607a; }
  .v3reuse .runline .ph { color:#16324F; font-weight:700; transition:color .3s; }
  .v3reuse .runline .ph.halt { color:#B98A2E; }
  .v3reuse .runline .ph.go { color:#1E6B52; }
  /* Waiting dots during the human gate (Round 7). The halt runs 5.6s — it is
     the climax of the whole animation and stays that long deliberately — but
     the belt pauses and the ghosts freeze during it, so for those seconds the
     diagram has no motion at all and reads as broken. These dots sit in the
     phase label, exactly where the eye already is, and say "still waiting on
     a person" rather than "this stopped working". */
  .v3reuse .runline .ph.halt::after {
    content:"···"; display:inline-block; width:0; overflow:hidden;
    vertical-align:bottom; letter-spacing:1px;
    animation:v3waitdots 1.6s steps(4) infinite;
  }
  @keyframes v3waitdots { to { width:2.2em; } }

  .v3reuse .belt { position:relative; height:56px; border-bottom:1px solid #E6E1D6; background:
          repeating-linear-gradient(90deg, transparent 0 46px, #EFEAE0 46px 48px); overflow:hidden; }
  .v3reuse .beltline { position:absolute; left:0; right:0; top:50%; height:2px; background:#E6E1D6; }
  .v3reuse .dossier { position:absolute; top:50%; transform:translate(-50%,-50%); left:6%;
             background:#fff; border:1.5px solid #16324F; color:#16324F;
             font-family:var(--mono); font-size:9.5px; font-weight:700; letter-spacing:.5px;
             padding:7px 12px; white-space:nowrap; transition:left 1.1s cubic-bezier(.5,0,.3,1), opacity .5s, border-color .3s, box-shadow .3s; }
  .v3reuse .dossier .st { color:#9C7A3C; }
  .v3reuse .dossier.held { border-color:#B98A2E; box-shadow:0 0 0 4px #B98A2E22; animation:v3held 1.4s ease-in-out infinite; }
  .v3reuse .dossier.cleared { border-color:#1E6B52; box-shadow:0 0 0 4px #1E6B5222; animation:none; }
  /* Loop states (GlassEngineV3): .entering snaps the dossier off-screen left
     with no transition so the slide-in animates cleanly; .exiting fades it
     out as it slides off the right at cycle end. */
  .v3reuse .dossier.entering { transition:none; opacity:0; }
  .v3reuse .dossier.exiting { opacity:0; }
  /* Round 7: was 0 0 0 8px #B98A2E14 — 8% alpha over a cream ground, which is
     invisible at arm's length. Widened and taken to ~35% so the held dossier
     visibly breathes for the whole 5.6s gate. */
  @keyframes v3held {
    0%,100% { box-shadow:0 0 0 4px #B98A2E22; }
    50%     { box-shadow:0 0 0 11px #B98A2E00, 0 0 0 4px #B98A2E59; }
  }
  .v3reuse .dossier.ghost { opacity:.32; border-color:#9AA1AB; color:#55607a; font-size:8.5px; padding:4px 9px;
                   animation:v3flow 14s linear infinite; transition:opacity .4s; }
  .v3reuse .belt.halted .dossier.ghost { animation-play-state:paused; opacity:.12; }
  .v3reuse .dossier.g1 { animation-delay:0s; top:32%; }
  .v3reuse .dossier.g2 { animation-delay:3.5s; top:68%; }
  .v3reuse .dossier.g3 { animation-delay:7s; top:32%; }
  .v3reuse .dossier.g4 { animation-delay:10.5s; top:68%; }
  .v3reuse .dossier.ghost.od { animation:v3flow 5s linear infinite; animation-delay:5s; top:50%; border-color:#9C7A3C; color:#9C7A3C; opacity:.6; }
  @keyframes v3flow { from { left:-8%; } to { left:108%; } }

  .v3reuse .stations { display:flex; }
  .v3reuse .station { flex:1; padding:18px 14px 20px; border-left:1px solid #E6E1D6; opacity:.45; transition:opacity .5s; position:relative; }
  .v3reuse .station:first-child { border-left:none; }
  .v3reuse .station.live { opacity:1; }
  .v3reuse .station .num { font-family:var(--mono); font-size:9.5px; letter-spacing:1.5px; color:#9C7A3C; font-weight:700; }
  .v3reuse .station h3 { font-family:var(--serif-memo); font-size:16px; color:#16324F; margin:6px 0 8px; }
  .v3reuse .station .art { height:104px; border:1px solid #E6E1D6; background:#fff; display:flex; align-items:center; justify-content:center; overflow:hidden; margin-bottom:8px; position:relative; }
  .v3reuse .station p { font-size:11.5px; color:#55607a; line-height:1.5; max-width:32ch; }
  .v3reuse .station p b { color:#14202C; }

  .v3reuse .tickroll { font-family:var(--mono); font-size:9.5px; color:#1E6B52; line-height:1.9; animation:v3roll 6s linear infinite; }
  @keyframes v3roll { from { transform:translateY(18px);} to { transform:translateY(-52px);} }
  .v3reuse .station:not(.live) .tickroll { animation-play-state:paused; }

  .v3reuse .comp { font-family:var(--mono); font-size:10px; text-align:left; padding:8px 10px; width:100%; }
  .v3reuse .comp .line { opacity:0; transition:opacity .4s; }
  .v3reuse .comp .line.show { opacity:1; }
  .v3reuse .comp .big { font-size:14px; font-weight:700; color:#14202C; }
  .v3reuse .comp .src { color:#1E6B52; font-size:8.5px; }

  .v3reuse .gauntlet { display:flex; flex-direction:column; gap:6px; width:100%; padding:8px 10px; }
  .v3reuse .insp { display:flex; align-items:center; gap:7px; font-family:var(--mono); font-size:9px; letter-spacing:.8px; text-transform:uppercase; color:#9AA1AB; font-weight:700; }
  .v3reuse .insp .dot { width:16px; height:16px; border-radius:50%; border:1.5px solid #E6E1D6; display:flex; align-items:center; justify-content:center; font-size:9px; color:transparent; transition:all .3s; flex-shrink:0; }
  .v3reuse .insp.pass { color:#14202C; }
  .v3reuse .insp.pass .dot { border-color:#1E6B52; background:#F0F6F2; color:#1E6B52; }

  .v3reuse .gateart { flex-direction:column; gap:6px; }
  .v3reuse .gatecirc { width:48px; height:48px; border-radius:50%; border:2px solid #E6E1D6; display:flex; align-items:center; justify-content:center; font-size:19px; color:#9AA1AB; transition:all .4s; background:#fff; }
  .v3reuse .gatecirc.wait { border-color:#B98A2E; color:#B98A2E; animation:v3gatepulse 1.4s ease-in-out infinite; }
  .v3reuse .gatecirc.open { border-color:#1E6B52; color:#1E6B52; background:#F0F6F2; animation:none; }
  /* Same correction as v3held above: the old 8%-alpha ring could not be seen.
     This one expands and fades like a radar ping, so the gate reads as
     actively waiting on a human rather than stalled. */
  @keyframes v3gatepulse {
    0%   { box-shadow:0 0 0 0 #B98A2E5C; }
    70%  { box-shadow:0 0 0 14px #B98A2E00; }
    100% { box-shadow:0 0 0 0 #B98A2E00; }
  }
  .v3reuse .gatestamp { font-family:var(--mono); font-size:9px; color:#55607a; opacity:0; transition:opacity .4s; text-align:center; }
  .v3reuse .gatestamp.show { opacity:1; }

  .v3reuse .xbook { position:absolute; right:8px; top:8px; font-family:var(--mono); font-size:9px; letter-spacing:1px;
           color:#9C7A3C; font-weight:700; border:1px solid #9C7A3C; padding:2px 6px; background:#fff;
           opacity:0; transition:opacity .5s; }
  .v3reuse .xbook.lit { opacity:1; }
  .v3reuse .fan { display:grid; grid-template-columns:1fr 1fr; gap:5px; padding:8px; width:100%; }
  .v3reuse .fan span { border:1px solid #E6E1D6; background:#fff; font-family:var(--mono); font-size:8.5px; letter-spacing:.6px; text-transform:uppercase; color:#9AA1AB; font-weight:700; text-align:center; padding:8px 4px; transition:all .3s; }
  .v3reuse .fan span.lit { background:#16324F; color:#F4F1E9; border-color:#16324F; }
  .v3reuse .fan span.fancoming { color:#B98A2E; border-color:#EAD9B8; background:#FBF6EA; }

  .v3reuse .engineclose { text-align:center; margin-top:36px; }
  .v3reuse .engineclose .line { font-family:var(--serif-memo); font-size:clamp(17px,2vw,22px); color:#16324F; font-weight:600; }
  /* The infrastructure sentence, moved down out of the intro paragraph.
     Sized between the punchline above it and the disclaimer below so it reads
     as substantiation rather than either a claim or fine print. */
  .v3reuse .engineclose .subfoot { font-size:13px; line-height:1.6; color:#55607a; max-width:62ch; margin:14px auto 0; }
  .v3reuse .engineclose .fine { font-size:12px; color:#9AA1AB; margin-top:14px; }

  @media(max-width:900px){
    .v3reuse .stations { flex-direction:column; }
    .v3reuse .station { border-left:none; border-top:1px solid #E6E1D6; }
    .v3reuse .belt { height:44px; }
    .v3reuse .dossier { font-size:8px; padding:5px 8px; }
    .v3reuse .dossier.ghost { font-size:7px; padding:3px 7px; }
  }

  /* The advisor's desk */
  .v3reuse .desk { border-top:1px solid #E6E1D6; padding:44px 0 56px; }
  .v3reuse .desk .lbl { font-family:var(--mono); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:#16324F; font-weight:700; text-align:center; }
  .v3reuse .desk h2 { font-family:var(--serif-memo); font-size:clamp(22px,2.6vw,32px); font-weight:600; text-align:center; margin-top:10px; letter-spacing:-.4px; }
  .v3reuse .desk .sub { text-align:center; color:#55607a; font-size:14px; max-width:58ch; margin:12px auto 0; }
  .v3reuse .deskpanel { margin:32px auto 0; border:1px solid #E6E1D6; background:#fff; }
  .v3reuse .deskbar { display:flex; gap:20px; flex-wrap:wrap; padding:14px 20px; border-bottom:1px solid #E6E1D6; font-family:var(--mono); font-size:10.5px; letter-spacing:.6px; text-transform:uppercase; color:#55607a; align-items:center; }
  .v3reuse .deskbar b { color:#14202C; font-size:14px; margin-right:4px; font-variant-numeric:tabular-nums; }
  .v3reuse .deskbar .ok b { color:#1E6B52; }
  .v3reuse .deskbar .esc b { color:#B98A2E; }
  .v3reuse .deskbar .clock { margin-left:auto; color:#55607a; }
  .v3reuse .livedot { color:#1E6B52 !important; font-weight:700; display:flex; align-items:center; gap:6px; }
  .v3reuse .livedot::before { content:""; width:7px; height:7px; border-radius:50%; background:#1E6B52; animation:v3blink 1.4s ease-in-out infinite; }
  @keyframes v3blink { 50% { opacity:.25; } }
  .v3reuse .dashgrid { display:grid; grid-template-columns:1fr 1fr; gap:0; }
  @media(max-width:820px){ .v3reuse .dashgrid { grid-template-columns:1fr; } }
  .v3reuse .dashcol { padding:14px 18px; }
  .v3reuse .dashcol:first-child { border-right:1px solid #E6E1D6; }
  @media(max-width:820px){ .v3reuse .dashcol:first-child { border-right:none; border-bottom:1px solid #E6E1D6; } }
  .v3reuse .colhd { font-family:var(--mono); font-size:10px; letter-spacing:1.4px; text-transform:uppercase; color:#55607a; font-weight:700; margin-bottom:10px; display:flex; justify-content:space-between; }
  .v3reuse .colhd.esc { color:#B98A2E; }
  .v3reuse .colhd span { color:#14202C; font-variant-numeric:tabular-nums; }

  .v3reuse .callrow { display:flex; align-items:center; gap:10px; border:1px solid #E6E1D6; background:#fff; padding:8px 11px; margin-bottom:7px; opacity:0; transform:translateY(8px); transition:opacity .4s, transform .4s; }
  .v3reuse .callrow.on { opacity:1; transform:none; }
  .v3reuse .callrow .lwave { display:flex; align-items:center; gap:2px; height:18px; width:30px; flex-shrink:0; }
  .v3reuse .callrow .lwave i { flex:1; background:#1E6B52; border-radius:1px; animation:v3lw 1s ease-in-out infinite; }
  .v3reuse .callrow .lwave i:nth-child(2){ animation-delay:.15s } .v3reuse .callrow .lwave i:nth-child(3){ animation-delay:.3s } .v3reuse .callrow .lwave i:nth-child(4){ animation-delay:.45s }
  @keyframes v3lw { 0%,100%{ height:4px } 50%{ height:16px } }
  .v3reuse .callrow .cwho { font-size:11.5px; }
  .v3reuse .callrow .cwho b { font-family:var(--serif-memo); color:#16324F; font-size:12.5px; display:block; }
  .v3reuse .callrow .cwho small { color:#9AA1AB; font-size:10px; }
  .v3reuse .callrow .ctimer { margin-left:auto; font-family:var(--mono); font-size:10px; color:#1E6B52; font-variant-numeric:tabular-nums; }
  .v3reuse .callrow .clisten { font-family:var(--mono); font-size:8.5px; letter-spacing:.8px; text-transform:uppercase; color:#55607a; border:1px solid #E6E1D6; padding:4px 8px; cursor:pointer; }

  .v3reuse .esccard { border:1px solid #E6E1D6; border-left:3px solid #B98A2E; padding:11px 14px; margin-bottom:7px; opacity:0; transform:translateY(10px); transition:opacity .5s, transform .5s; }
  .v3reuse .esccard.on { opacity:1; transform:none; }
  .v3reuse .esccard .q { font-family:var(--serif-memo); font-size:13px; color:#14202C; line-height:1.35; }
  .v3reuse .esccard .why { font-size:11px; color:#55607a; margin-top:6px; }
  .v3reuse .esccard .row { display:flex; align-items:center; justify-content:space-between; margin-top:10px; gap:8px; }
  .v3reuse .esccard .who { font-family:var(--mono); font-size:9px; color:#9AA1AB; letter-spacing:.5px; }
  .v3reuse .esccard .join { font-family:var(--mono); font-size:9px; letter-spacing:1px; text-transform:uppercase; font-weight:700; color:#fff; background:#16324F; padding:6px 12px; cursor:pointer; white-space:nowrap; }
  .v3reuse .esccard.fresh { animation:v3flashin .8s ease; }
  @keyframes v3flashin { 0%{ background:#FBF6EA } 100%{ background:#fff } }
  .v3reuse .deskfoot { padding:11px 20px 14px; border-top:1px solid #E6E1D6; font-size:11.5px; color:#9AA1AB; display:flex; justify-content:space-between; flex-wrap:wrap; gap:8px; }
  .v3reuse .deskfoot b { color:#1E6B52; }

  @media (prefers-reduced-motion: reduce){
    .v3reuse .pane, .v3reuse .pane .body, .v3reuse .dossier { transition:none; }
    .v3reuse .pane.collapsed .body { transform:none; }
    .v3reuse .tickroll, .v3reuse .dossier.held, .v3reuse .gatecirc.wait { animation:none; }
    .v3reuse .dossier.ghost { animation:none; opacity:.2; }
    .v3reuse *{ animation:none !important; }
  }
`;
